import { atom, getDefaultStore, useAtomValue } from "jotai";

/**
 * Per thread, the canvas root the user presented while the panel was collapsed —
 * the content that is laid over the screen instead of shown in the canvas panel.
 *
 * Revealing the panel stays user-owned: someone who hid the calendar meant it,
 * and restoring it to show a contact they clicked would undo a choice they made.
 * So the card goes over the thread they are reading and the panel stays as they
 * left it — the same principle `canvasUpdateBadge` follows when it badges a
 * server push rather than opening anything.
 *
 * It stores the URN rather than a bare flag because that is what keeps a server
 * push off the screen. `thread_canvas:update` replaces the stack in place, so a
 * per-thread flag would still be set when the agent's card arrived and would
 * show it over the conversation unasked. Ownership is checked by comparing this
 * against the live stack root: a drill-down keeps the root and stays overlaid,
 * while anything that replaces the root — a server write, an emptied stack —
 * ends the overlay and leaves the badge to do the telling.
 *
 * Client-only and keyed by thread, mirroring `canvasUpdateBadge`. A single jotai
 * atom holds the map so `getSnapshot` can read it outside React while the thread
 * view reads it reactively.
 */
const overlaidCanvasRootsAtom = atom<ReadonlyMap<number, string>>(new Map<number, string>());

/** Lay `urn` over the screen as this thread's presented root. See the atom above. */
export function markCanvasScreenOverlay(threadId: number, urn: string): void {
  const store = getDefaultStore();
  const prev = store.get(overlaidCanvasRootsAtom);
  if (prev.get(threadId) === urn) return;
  store.set(overlaidCanvasRootsAtom, new Map(prev).set(threadId, urn));
}

/**
 * Stop overlaying this thread's canvas content — the user dismissed it, opened
 * the panel, or something replaced the root they presented.
 *
 * Clearing matters beyond the card on screen: a stale entry that happened to
 * match a later root would put content on screen the user never asked for.
 */
export function clearCanvasScreenOverlay(threadId: number): void {
  const store = getDefaultStore();
  const prev = store.get(overlaidCanvasRootsAtom);
  if (!prev.has(threadId)) return;
  const next = new Map(prev);
  next.delete(threadId);
  store.set(overlaidCanvasRootsAtom, next);
}

/** Read the presented root outside React, for the send-path view snapshot. */
export function readCanvasScreenOverlay(threadId: number): string | undefined {
  return getDefaultStore().get(overlaidCanvasRootsAtom).get(threadId);
}

/** Reactively read the presented root, so the thread view can render the overlay. */
export function useCanvasScreenOverlay(threadId: number): string | undefined {
  return useAtomValue(overlaidCanvasRootsAtom).get(threadId);
}
